import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import type { ExpandableConfig } from "rc-table/lib/interface";
import { useIntl } from "react-intl";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Person from "@mapmycustomers/shared/types/entity/Person";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import AnnotationEditModal from "@app/component/activity/ActivityAnnotation/AnnotationEditModal";
import EditActivityModal from "@app/component/createEditEntity/Activity/EditActivityModal";
import Table from "@app/component/Table";
import ListViewPagination from "@app/component/view/ListView/components/Pagination/ListViewPagination";
import ListViewComponent from "@app/component/view/ListView/types/ListViewComponent";
import useListView from "@app/component/view/ListView/utils/useListView";
import mergeFilterModels from "@app/component/view/utils/mergeFilterModels";
import { showEntityView } from "@app/store/entityView/actions";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import useSizeObserver from "@app/util/hook/useSizeObserver";

import {
  deselectAll,
  toggleActivitySelection,
  toggleSelectAll,
  updateNote,
} from "../../store/actions";
import {
  areAllSelected,
  getList,
  getLoading,
  getSelected,
  getTotalFilteredRecordsCount,
  getUpdateNoteLoadingIds,
} from "../../store/selectors";
import useColumns from "../../utils/useColumns";

import styles from "./ActivityTable.module.scss";
import ChildActivitiesTable from "./component/ChildActivitiesTable";

const ALLOWED_ACTIVITY_TABLE_PAGE_SIZES = [100, 250, 500];

interface Props {
  activities: Activity[];
  areAllSelected: boolean;
  isLoading: boolean;
  isReadOnlyMember: boolean;
  onDeselectAll: typeof deselectAll;
  selected: Activity[];
  showEntityView: typeof showEntityView;
  toggleActivitySelection: typeof toggleActivitySelection;
  toggleSelectAll: typeof toggleSelectAll;
  totalFilteredRecords: number;
  updateNote: typeof updateNote.request;
  updateNoteLoadingIds: Activity["id"][];
}

const ActivityTable: React.FC<Props> = ({
  activities,
  areAllSelected,
  isLoading,
  isReadOnlyMember,
  onDeselectAll,
  selected,
  showEntityView,
  toggleActivitySelection,
  toggleSelectAll,
  totalFilteredRecords,
  updateNote,
  updateNoteLoadingIds,
}) => {
  const analytics = useAnalytics();

  const intl = useIntl();
  const { register, unregister, updateViewStateAndFetch, viewState } = useListView();
  const [selectedActivity, selectActivity] = useState<Activity | undefined>();

  const handleExternalFilterChanged: ListViewComponent<Activity>["onFilterChange"] =
    useDynamicCallback((newFilterModel: Partial<FilterModel>) => {
      // first of all, update the list view state
      const resultingFilterModel = mergeFilterModels(viewState.filter, newFilterModel);
      updateViewStateAndFetch({
        filter: resultingFilterModel,
        range: { endRow: viewState.range.endRow - viewState.range.startRow, startRow: 0 },
      });
    });

  const handlePreviewCompany = useCallback(
    (companyId: Company["id"]) => {
      showEntityView({ entityId: companyId, entityType: EntityType.COMPANY });
      analytics.clicked(["Account in Table Row"]);
    },
    [showEntityView, analytics]
  );
  const handlePreviewDeal = useCallback(
    (dealId: Deal["id"]) => {
      showEntityView({ entityId: dealId, entityType: EntityType.DEAL });
      analytics.clicked(["Deal in Table Row"]);
    },
    [showEntityView, analytics]
  );
  const handlePreviewPerson = useCallback(
    (personId: Person["id"]) => {
      showEntityView({ entityId: personId, entityType: EntityType.PERSON });
      analytics.clicked(["Contact in Table Row"]);
    },
    [showEntityView, analytics]
  );
  const rowEventHandlers = useCallback(
    (record: Activity) => ({
      onClick: (e: any) => {
        // This is not a good solution but the problem here is that this get triggered if we preview some file by clicking file icon on every activity. Please suggest a better way to do this.
        if (e.target.classList?.contains("ant-table-cell") || e.target.closest(".ant-table-cell")) {
          selectActivity(record);
        }
        analytics.clicked(["Activity in Table Row"]);
      },
    }),
    [selectActivity, analytics]
  );
  const handleHideActivityInfo = useCallback(
    (updated?: boolean) => {
      if (updated) {
        updateViewStateAndFetch({});
      }
      selectActivity(undefined);
    },
    [updateViewStateAndFetch, selectActivity]
  );

  const handleToggleChange = useCallback(() => {
    toggleSelectAll();
    analytics.clicked(["Checkbox to Select All"]);
  }, [toggleSelectAll, analytics]);

  const handleUpdateNote = useCallback(
    (data: { activity: Activity; onSuccess: () => void; updated?: boolean }) => {
      updateNote(data);
      analytics.clicked(["Add Note in Table Row"]);
    },
    [updateNote, analytics]
  );

  const [width, , ref] = useSizeObserver();
  const columns = useColumns({
    areAllSelected,
    intl,
    isLoading,
    isReadOnlyMember,
    onCompanyClick: handlePreviewCompany,
    onDealClick: handlePreviewDeal,
    onPersonClick: handlePreviewPerson,
    selected,
    toggleActivitySelection,
    toggleSelectAll: handleToggleChange,
    updateNote: handleUpdateNote,
    updateNoteLoadingIds,
    width,
  });

  useEffect(() => {
    const callbacks: ListViewComponent<AnyEntity> = {
      onFilterChange: handleExternalFilterChanged,
    };
    register(callbacks);
    return () => unregister(callbacks);
  }, [handleExternalFilterChanged, register, unregister]);

  useEffect(() => updateViewStateAndFetch({}), [updateViewStateAndFetch]);

  useEffect(() => {
    // reset selected rows when filter changes
    onDeselectAll();
  }, [viewState.filter, onDeselectAll]);

  // When relationship filters are used, we fetch all activities - parent and child -
  // and hence don't need to make any rows expandable.
  const hasRelationshipFilters = (Object.keys(viewState.filter) as ActivityFieldName[]).some(
    (name) =>
      [ActivityFieldName.COMPANY, ActivityFieldName.DEAL, ActivityFieldName.PERSON].includes(name)
  );
  const expandableProps: ExpandableConfig<Activity> = useMemo(
    () => ({
      expandedRowRender: hasRelationshipFilters
        ? undefined
        : (record: Activity, index, indent, expanded) =>
            expanded ? (
              <ChildActivitiesTable
                columns={columns}
                onActivityClick={selectActivity}
                parentActivityId={record.id}
              />
            ) : null,
      indentSize: 0,
      rowExpandable: (activity: Activity) =>
        !hasRelationshipFilters && !!activity.emailLogId && !!activity.childCount,
    }),
    [columns, hasRelationshipFilters]
  );

  const renderSelectedActivity = () => {
    if (!selectedActivity) {
      return null;
    }

    if (!selectedActivity.childCount) {
      return <EditActivityModal activityId={selectedActivity.id} onHide={handleHideActivityInfo} />;
    }

    return <AnnotationEditModal activityId={selectedActivity.id} onHide={handleHideActivityInfo} />;
  };

  return (
    <div className={styles.container} ref={ref}>
      <Table<Activity>
        className={styles.table}
        clickableRows
        columns={columns}
        dataSource={activities}
        expandable={expandableProps}
        loading={isLoading}
        onRow={rowEventHandlers}
        pagination={false}
        rowKey="id"
        scrollable
        showBorder
      />
      <ListViewPagination
        allowedPageSizes={ALLOWED_ACTIVITY_TABLE_PAGE_SIZES}
        showSizeChanger
        totalRecords={totalFilteredRecords}
      />
      {renderSelectedActivity()}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activities: getList(state),
  areAllSelected: areAllSelected(state),
  isLoading: getLoading(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  selected: getSelected(state),
  totalFilteredRecords: getTotalFilteredRecordsCount(state),
  updateNoteLoadingIds: getUpdateNoteLoadingIds(state),
});

const mapDispatchToProps = {
  onDeselectAll: deselectAll,
  showEntityView,
  toggleActivitySelection,
  toggleSelectAll,
  updateNote: updateNote.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityTable);
